import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, Bell, CheckCircle2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageHeader, FullPageSpinner } from "@/components/kit";
import { inputClass } from "@/components/AuthShell";

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`h-7 w-12 rounded-full transition-colors relative shrink-0 ${checked ? "bg-[#6F8F72]" : "bg-[#E8E3DA]"}`}
    >
      <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${checked ? "left-6" : "left-1"}`} />
    </button>
  );
}

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Phone verification flow
  const [phoneInput, setPhoneInput] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("idle"); // "idle" | "code"
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiFetch("/notifications/preferences")
      .then(setPrefs)
      .catch(() =>
        setPrefs({ push_enabled: true, email_enabled: true, sms_enabled: false, phone: "", phone_verified: false })
      )
      .finally(() => setLoading(false));
  }, []);

  const save = async (next) => {
    setPrefs(next);
    setSaving(true);
    try {
      await apiFetch("/notifications/preferences", { method: "PUT", body: JSON.stringify(next) });
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const onSmsToggle = (v) => {
    if (v && !prefs.phone_verified) {
      // Reveal verification panel without persisting SMS-on yet.
      setStep("idle");
      setPhoneInput(prefs.phone || "");
      setPrefs({ ...prefs, sms_enabled: true });
      return;
    }
    save({ ...prefs, sms_enabled: v });
  };

  const sendCode = async () => {
    setBusy(true);
    try {
      const res = await apiFetch("/notifications/phone/send-code", {
        method: "POST",
        body: JSON.stringify({ phone: phoneInput }),
      });
      setStep("code");
      toast.success(`Verification code sent to ${res.phone}`);
    } catch (err) {
      toast.error(err.message || "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    try {
      const updated = await apiFetch("/notifications/phone/verify-code", {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      setPrefs(updated);
      setStep("idle");
      setCode("");
      toast.success("Phone verified — SMS alerts are on! 🐾");
    } catch (err) {
      toast.error(err.message || "Could not verify code");
    } finally {
      setBusy(false);
    }
  };

  const changeNumber = () => {
    setStep("idle");
    setCode("");
    setPhoneInput(prefs.phone || "");
    setPrefs({ ...prefs, phone_verified: false });
  };

  if (loading) return <FullPageSpinner />;

  const rows = [
    { key: "email_enabled", icon: Mail, title: "Email notifications", body: "Reminders and important account emails." },
    { key: "push_enabled", icon: Bell, title: "Push notifications", body: "Sent to the Wooflect mobile app." },
    { key: "sms_enabled", icon: MessageSquare, title: "SMS text alerts", body: "Reminders + emergency alerts to your phone." },
  ];

  const showVerifyPanel = prefs.sms_enabled && !prefs.phone_verified;

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader title="Notifications" subtitle="Choose how we reach you." backTo="/app/profile" />

      <div className="rounded-3xl bg-white border border-[#E8E3DA] overflow-hidden">
        {rows.map((r, i) => (
          <div key={r.key} className={`flex items-center gap-3 p-4 ${i !== 0 ? "border-t border-[#E8E3DA]" : ""}`}>
            <div className="h-10 w-10 rounded-2xl bg-[#F2EFE9] flex items-center justify-center shrink-0">
              <r.icon size={18} color="#4A4746" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold">{r.title}</p>
              <p className="text-sm text-[#4A4746]">{r.body}</p>
            </div>
            <Toggle
              testId={`notif-toggle-${r.key}`}
              checked={!!prefs[r.key]}
              onChange={(v) => (r.key === "sms_enabled" ? onSmsToggle(v) : save({ ...prefs, [r.key]: v }))}
            />
          </div>
        ))}
      </div>

      {/* Verified phone summary */}
      {prefs.sms_enabled && prefs.phone_verified && (
        <div className="mt-4 flex items-center gap-2 rounded-2xl bg-[#EEF4EF] border border-[#D5E4D7] p-4" data-testid="notif-phone-verified">
          <CheckCircle2 size={18} color="#4E7B54" />
          <p className="text-sm text-[#3C5A41] flex-1">
            Verified: <span className="font-bold">{prefs.phone}</span>
          </p>
          <button data-testid="notif-change-number" onClick={changeNumber} className="text-sm font-bold text-[#B8611C]">
            Change
          </button>
        </div>
      )}

      {/* Verification panel */}
      {showVerifyPanel && (
        <div className="mt-4 rounded-2xl bg-white border border-[#E8E3DA] p-4">
          {step === "idle" ? (
            <>
              <label className="block text-sm font-bold mb-1.5">Verify your phone number</label>
              <input
                data-testid="notif-phone-input"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="+1 555 123 4567"
                className={inputClass}
              />
              <p className="text-xs text-[#9A938B] mt-1.5">We&apos;ll text you a 6-digit code to confirm it&apos;s you. Msg &amp; data rates may apply.</p>
              <button
                data-testid="notif-send-code"
                onClick={sendCode}
                disabled={busy || !phoneInput.trim()}
                className="mt-3 w-full rounded-full bg-[#6F8F72] text-white font-bold py-3 disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send code"}
              </button>
            </>
          ) : (
            <>
              <label className="block text-sm font-bold mb-1.5">Enter the 6-digit code</label>
              <input
                data-testid="notif-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                className={inputClass}
              />
              <div className="flex gap-2 mt-3">
                <button
                  data-testid="notif-verify-code"
                  onClick={verifyCode}
                  disabled={busy || code.length < 6}
                  className="flex-1 rounded-full bg-[#6F8F72] text-white font-bold py-3 disabled:opacity-50"
                >
                  {busy ? "Verifying…" : "Verify"}
                </button>
                <button
                  onClick={sendCode}
                  disabled={busy}
                  className="rounded-full border border-[#E8E3DA] font-bold py-3 px-4 text-[#4A4746]"
                >
                  Resend
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {saving && <p className="text-xs text-[#9A938B] text-center mt-4">Saving…</p>}
    </div>
  );
}
